import firebase from "firebase/compat/app";

/** Firestore collection names — kept inline to avoid circular imports via models. */
const COLLECTIONS = {
  orders: "orders",
  orderTemplates: "order_templates",
  customers: "customers",
  products: "products",
  locations: "locations",
  vehicles: "vehicles",
  companies: "companies",
  drivers: "drivers",
  parameters: "configuration_parameters",
  workspaces: "workspaces",
  workspaceSettings: "workspace_settings",
  users: "users",
  workspaceAccesses: "workspaceAccesses",
} as const;

/**
 * Centralized React Query key factory — the single source of truth for every
 * cache key used by the application data layer.
 *
 * Why this exists:
 *  - Previously each hook hand-wrote its own key array. Small inconsistencies
 *    (e.g. `useDrivers` omitting the cache key, `useLocations` concatenating the
 *    workspace id into the collection name) meant the same logical dataset could
 *    be cached under different keys, defeating React Query's request
 *    deduplication and breaking cache invalidation.
 *  - Keeping every key in one place makes it trivial to answer "who is using
 *    this data?" and "how is it invalidated?" and guarantees that two callers
 *    asking for the same dataset hit the exact same cache entry.
 *
 * Convention: keys are tuples of the form
 *   [collectionName, cacheKey, ...scopes]
 * where `cacheKey` is the workspace-wide cache-busting token from CacheContext.
 */

type WhereTuple = [string, firebase.firestore.WhereFilterOp, string];

export const queryKeys = {
  invoiceCandidates: (workspaceId: string, customerId?: string) => customerId
    ? ["invoiceCandidates", workspaceId, customerId]
    : ["invoiceCandidates", workspaceId],
  /* ----------------------------- Firestore reads ---------------------------- */

  customers: (cacheKey: string | undefined, workspaceId: string) =>
    [COLLECTIONS.customers, cacheKey, workspaceId],

  products: (cacheKey: string | undefined, workspaceId: string) =>
    [COLLECTIONS.products, cacheKey, workspaceId],

  orderTemplates: (cacheKey: string | undefined, workspaceId: string) =>
    [COLLECTIONS.orderTemplates, cacheKey, workspaceId],

  locations: (
    cacheKey: string | undefined,
    workspaceId: string,
    additionalWhere: WhereTuple[] = [],
  ) => [COLLECTIONS.locations, cacheKey, workspaceId, additionalWhere],

  vehicles: (cacheKey: string | undefined, workspaceId: string) =>
    [COLLECTIONS.vehicles, cacheKey, workspaceId],

  companies: (cacheKey: string | undefined, workspaceId: string) =>
    [COLLECTIONS.companies, cacheKey, workspaceId],

  drivers: (cacheKey: string | undefined, workspaceId: string) =>
    [COLLECTIONS.drivers, cacheKey, workspaceId],

  parameters: (
    cacheKey: string | undefined,
    workspaceId: string,
    type: string,
  ) => [COLLECTIONS.parameters, cacheKey, workspaceId, type],

  workspaces: (cacheKey: string | undefined) =>
    [COLLECTIONS.workspaces, cacheKey],

  workspaceSettings: (cacheKey: string | undefined, workspaceId: string) =>
    [COLLECTIONS.workspaceSettings, cacheKey, workspaceId],

  users: (cacheKey: string | undefined) => [COLLECTIONS.users, cacheKey],

  allUsers: (cacheKey: string | undefined) =>
    [COLLECTIONS.users, "all", cacheKey],

  userByEmail: (email: string) => [COLLECTIONS.users, { email }],

  workspaceUsers: (cacheKey: string | undefined, workspaceId: string) =>
    [COLLECTIONS.workspaceAccesses, cacheKey, workspaceId],

  userWorkspaces: (cacheKey: string | undefined, email: string) =>
    [COLLECTIONS.workspaceAccesses, cacheKey, email],

  /* ------------------------------- API reads -------------------------------- */

  ordersApi: (workspaceId: string) => ["ordersApi", workspaceId],

  /**
   * Infinite (cursor-paginated) orders key. The cursor is NOT part of the key —
   * it is the `pageParam` of the infinite query. Only the filters/search live in
   * the key so that changing a filter starts a fresh paged list while sharing
   * the `ordersApi` prefix for invalidation.
   */
  ordersApiInfinite: (
    workspaceId: string,
    filters: { statuses?: string[]; driverId?: string; search?: string } = {},
  ) => ["ordersApi", "infinite", workspaceId, filters],

  invoicesApi: (workspaceId: string) => ["invoicesApi", "infinite", workspaceId],

  driversApi: (workspaceId: string) => ["driversApi", workspaceId],

  driverRoutes: (driverId: string, stopDate: string) =>
    ["driverRoutes", driverId, stopDate],

  routeDayOverview: (workspaceId: string, date: string) =>
    ["routeDayOverview", workspaceId, date],
};

/**
 * Collection-name prefixes used by invalidation helpers to wipe every cached
 * variant of a dataset regardless of its workspace / filter scope.
 */
export const collectionPrefixes = {
  orders: COLLECTIONS.orders,
  orderTemplates: COLLECTIONS.orderTemplates,
  customers: COLLECTIONS.customers,
  products: COLLECTIONS.products,
  locations: COLLECTIONS.locations,
  vehicles: COLLECTIONS.vehicles,
  companies: COLLECTIONS.companies,
  drivers: COLLECTIONS.drivers,
  parameters: COLLECTIONS.parameters,
  workspaces: COLLECTIONS.workspaces,
  workspaceSettings: COLLECTIONS.workspaceSettings,
  users: COLLECTIONS.users,
  workspaceAccesses: COLLECTIONS.workspaceAccesses,
  ordersApi: "ordersApi",
  invoicesApi: "invoicesApi",
  driversApi: "driversApi",
} as const;
